import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { EditOutlined as EditOutlinedIcon } from "../icons/FigmaControlIcons";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { sanitizeImportedSourceText } from "../../utils/importTextSanitizers";
import MentionableNoteField from "../MentionableNoteField";
import { formatLocalDate, formatLocalTime, parseDateTimeValue, toTimeValue } from "../../utils/dateTime";
import {
  SIDEBAR_WIDGET_BORDER_COLOR,
  SIDEBAR_WIDGET_CARD_SX,
  SIDEBAR_WIDGET_TITLE_SX,
} from "./sidebarWidgetStyles";

const BORDER = SIDEBAR_WIDGET_BORDER_COLOR;
const MUTED = "#98A2B3";
const BODY = "#475467";
const PURPLE = "#9C4FFF";

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    ""
  );
}

function getAuthHeaders() {
  const token = String(getToken() || "").trim();
  if (!token) return {};

  const rawToken = token.replace(/^Bearer\s+/i, "");
  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
}

function normalizeNoteItem(activity) {
  if (!activity || typeof activity !== "object") return null;

  const normalizedType = String(activity.activity_type || activity.type || "")
    .trim()
    .toLowerCase();
  if (normalizedType && normalizedType !== "note" && normalizedType !== "comment") {
    return null;
  }

  const details = sanitizeImportedSourceText(activity.details || activity.summary || "");
  if (!details) return null;

  const metadata =
    activity.metadata && typeof activity.metadata === "object" ? activity.metadata : {};
  const actorName = String(
    activity.actor_name || metadata.actor_name || metadata.actor || ""
  ).trim();
  const numericId = Number(activity.id);
  const persisted = Number.isFinite(numericId) && numericId > 0;

  return {
    id: activity.id || `note-${Date.now()}`,
    title: String(activity.title || "Note").trim() || "Note",
    body: details,
    actorName,
    createdAt: activity.created_at || activity.createdAt || null,
    persisted,
  };
}

function buildLegacyNoteItem(notesText, jobId) {
  const body = sanitizeImportedSourceText(notesText);
  if (!body) return null;

  return {
    id: `legacy-note-${jobId || "job"}`,
    title: "Note",
    body,
    actorName: "",
    createdAt: null,
    persisted: false,
  };
}

function formatNoteDate(value) {
  if (!value) return "Recently added";

  const date = parseDateTimeValue(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const monthDay = formatLocalDate(date, {
    month: "short",
    day: "numeric",
  });
  const time = formatLocalTime(date, {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${monthDay} at ${time}`;
}

function MentionText({ text }) {
  const parts = String(text || "").split(
    /(@[A-Za-z][\w.-]*(?:\s+[A-Za-z][\w.-]*)?)/g
  );

  return (
    <Typography
      sx={{
        mt: 1.15,
        fontFamily: "Be Vietnam Pro",
        fontSize: 14,
        fontWeight: 400,
        color: BODY,
        lineHeight: 1.7,
        whiteSpace: "pre-line",
      }}
    >
      {parts.map((part, index) =>
        part && part.startsWith("@") ? (
          <Box
            key={`${part}-${index}`}
            component="span"
            sx={{ color: PURPLE, fontWeight: 500 }}
          >
            {part}
          </Box>
        ) : (
          part || ""
        )
      )}
    </Typography>
  );
}

export default function JobNotesCard({
  apiBase,
  entityId,
  entityType = "job",
  activities = [],
  notesText = "",
  onRefresh,
  onOpenAllNotes,
}) {
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerValue, setComposerValue] = useState("");
  const [composerSaving, setComposerSaving] = useState(false);
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [busyDeleteId, setBusyDeleteId] = useState(null);

  const notes = useMemo(() => {
    const normalizedNotes = (Array.isArray(activities) ? activities : [])
      .map((activity) => normalizeNoteItem(activity))
      .filter(Boolean)
      .sort((left, right) => {
        const leftTime = toTimeValue(left?.createdAt);
        const rightTime = toTimeValue(right?.createdAt);
        return rightTime - leftTime;
      });

    if (normalizedNotes.length) return normalizedNotes;

    const fallbackNote = buildLegacyNoteItem(notesText, entityId);
    return fallbackNote ? [fallbackNote] : [];
  }, [activities, entityId, notesText]);

  const previousNotes = useMemo(() => notes.slice(0, 1), [notes]);

  const closeComposer = useCallback(() => {
    if (composerSaving) return;
    setComposerOpen(false);
    setComposerValue("");
    setMentionedUserIds([]);
  }, [composerSaving]);

  const closeEdit = useCallback(() => {
    if (editSaving) return;
    setEditingNote(null);
    setEditValue("");
  }, [editSaving]);

  const handleCreateNote = useCallback(async () => {
    const nextBody = String(composerValue || "").trim();
    const numericEntityId = Number(entityId);
    if (!nextBody || !Number.isFinite(numericEntityId) || numericEntityId <= 0 || composerSaving) {
      return;
    }

    setComposerSaving(true);
    try {
      const response = await fetch(`${apiBase}/api/activities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({
          entityType,
          entityId: numericEntityId,
          activityType: "note",
          title: "Note",
          summary: nextBody.split("\n")[0].slice(0, 255),
          details: nextBody,
          mentionedUserIds,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to create note (${response.status})`);
      }

      setComposerOpen(false);
      setComposerValue("");
      await onRefresh?.(payload.activity);
    } catch (error) {
      console.error(`Failed to create ${entityType} note:`, error);
      window.alert(error?.message || "Failed to create note.");
    } finally {
      setComposerSaving(false);
    }
  }, [apiBase, composerSaving, composerValue, entityId, entityType, mentionedUserIds, onRefresh]);

  const handleStartEdit = useCallback((note) => {
    if (!note?.persisted) return;
    setEditingNote(note);
    setEditValue(String(note.body || "").trim());
  }, []);

  const handleSaveEdit = useCallback(async () => {
    const activityId = Number(editingNote?.id || 0);
    const nextBody = String(editValue || "").trim();
    if (!Number.isFinite(activityId) || activityId <= 0 || !nextBody || editSaving) return;

    setEditSaving(true);
    try {
      const response = await fetch(
        `${apiBase}/api/activities/${encodeURIComponent(activityId)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          credentials: "include",
          body: JSON.stringify({
            activityType: "note",
            title: editingNote?.title || "Note",
            summary: nextBody.split("\n")[0].slice(0, 255),
            details: nextBody,
          }),
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to update note (${response.status})`);
      }

      setEditingNote(null);
      setEditValue("");
      await onRefresh?.();
    } catch (error) {
      console.error("Failed to update job note:", error);
      window.alert(error?.message || "Failed to update note.");
    } finally {
      setEditSaving(false);
    }
  }, [apiBase, editSaving, editValue, editingNote, onRefresh]);

  const handleDelete = useCallback(
    async (note) => {
      const activityId = Number(note?.id || 0);
      if (!Number.isFinite(activityId) || activityId <= 0 || busyDeleteId === activityId) {
        return;
      }

      const confirmed = window.confirm("Delete this note? This cannot be undone.");
      if (!confirmed) return;

      setBusyDeleteId(activityId);
      try {
        const response = await fetch(
          `${apiBase}/api/activities/${encodeURIComponent(activityId)}`,
          {
            method: "DELETE",
            headers: {
              ...getAuthHeaders(),
            },
            credentials: "include",
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to delete note (${response.status})`);
        }

        await onRefresh?.();
      } catch (error) {
        console.error("Failed to delete job note:", error);
        window.alert(error?.message || "Failed to delete note.");
      } finally {
        setBusyDeleteId(null);
      }
    },
    [apiBase, busyDeleteId, onRefresh]
  );

  return (
    <>
      <Box
        sx={{
          ...SIDEBAR_WIDGET_CARD_SX,
          px: 4,
          py: 4,
        }}
      >
        <Typography
          sx={{
            ...SIDEBAR_WIDGET_TITLE_SX,
          }}
        >
          Notes
        </Typography>

        <Box
          role="button"
          tabIndex={0}
          onClick={() => setComposerOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setComposerOpen(true);
            }
          }}
          sx={{
            mt: 3,
            minHeight: 78,
            borderRadius: "12px",
            border: "1.5px dashed rgba(156, 79, 255, 0.7)",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            transition: "background-color 140ms ease, border-color 140ms ease",
            "&:hover": {
              borderColor: PURPLE,
              backgroundColor: "rgba(156, 79, 255, 0.04)",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 14,
              fontWeight: 500,
              color: PURPLE,
            }}
          >
            Add New Note
          </Typography>
        </Box>

        <Typography
          sx={{
            mt: 3.3,
            fontFamily: "Be Vietnam Pro",
            fontSize: 14,
            fontWeight: 600,
            color: "#101828",
          }}
        >
          Previous Notes
        </Typography>

        {previousNotes.length ? (
          <>
            {previousNotes.map((note) => (
              <Box key={note.id} sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
                    <Typography
                      sx={{
                        fontFamily: "Be Vietnam Pro",
                        fontSize: 13,
                        fontWeight: 400,
                        color: MUTED,
                      }}
                    >
                      {formatNoteDate(note.createdAt)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      onClick={() => handleStartEdit(note)}
                      aria-label="Edit note"
                      disabled={!note.persisted}
                      sx={{ width: 28, height: 28, color: PURPLE }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(note)}
                      aria-label="Delete note"
                      disabled={!note.persisted || busyDeleteId === Number(note.id || 0)}
                      sx={{ width: 28, height: 28, color: PURPLE }}
                    >
                      <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>

                <MentionText text={note.body} />

                <Typography
                  sx={{
                    mt: 0.75,
                    fontFamily: "Be Vietnam Pro",
                    fontSize: 12,
                    fontWeight: 400,
                    color: MUTED,
                  }}
                >
                  Added by: {note.actorName || "System"}
                </Typography>
              </Box>
            ))}
          </>
        ) : (
          <Typography
            sx={{
              mt: 1.5,
              fontFamily: "Be Vietnam Pro",
              fontSize: 13,
              color: MUTED,
            }}
          >
            No previous notes yet.
          </Typography>
        )}

        <Box sx={{ mt: 1.75, borderTop: `1px solid ${BORDER}` }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.15 }}>
          <Typography
            onClick={onOpenAllNotes}
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 14,
              fontWeight: 500,
              color: PURPLE,
              cursor: "pointer",
              userSelect: "none",
              "&:hover": {
                opacity: 0.88,
              },
            }}
          >
            View All Notes
          </Typography>
        </Box>
      </Box>

      <Dialog open={composerOpen} onClose={closeComposer} fullWidth maxWidth="sm">
        <DialogTitle data-popup-header sx={{ fontFamily: "Be Vietnam Pro", fontSize: 18, fontWeight: 700 }}>
          Add Note
        </DialogTitle>
        <DialogContent>
          <MentionableNoteField
            autoFocus
            fullWidth
            multiline
            minRows={5}
            value={composerValue}
            onChange={(event) => setComposerValue(event.target.value)}
            onMentionedUserIdsChange={setMentionedUserIds}
            placeholder={`Add a note for this ${entityType}`}
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeComposer} disabled={composerSaving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateNote}
            disabled={!String(composerValue || "").trim() || composerSaving}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              backgroundColor: PURPLE,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: PURPLE,
                boxShadow: "none",
              },
            }}
          >
            {composerSaving ? "Saving..." : "Save Note"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingNote)} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle data-popup-header sx={{ fontFamily: "Be Vietnam Pro", fontSize: 18, fontWeight: 700 }}>
          Edit Note
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={5}
            value={editValue}
            onChange={(event) => setEditValue(event.target.value)}
            placeholder="Update this note"
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeEdit} disabled={editSaving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={!String(editValue || "").trim() || editSaving}
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              backgroundColor: PURPLE,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: PURPLE,
                boxShadow: "none",
              },
            }}
          >
            {editSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
